import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'

import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'

import Card from '@components/card'
import PrimaryButton from '@components/inputs/button/primaryButton'
import Loading from '@components/loading'
import DiabetesStatus from './filters/diabetesStatus'
import HyperTensionAndDyslipdemiaStatus from './filters/hypertesionAndDyslipdemiaStatus'
import LifeHabitsStatus from './filters/lifeHabitsStatus'
import OverweigthStatus from './filters/overweightStatus'
import RiskStatus from './filters/riskStatus'
import engagementBeneficiaryStatus from './filters/engagementBeneficiaryStatus'
import equipmentStatus from './filters/equipmentStatus'

import excelFile from '../../assets/sheets/indicators-spreadsheet.xlsx'
import { getMeterValue } from '@util/meterUtil'

import { statusFilter, statusTypes } from '@generators/tresholdConstants'
import colors from '@settings/styleSettings'

import 'material-icons/iconfont/filled.css'
import 'material-icons/iconfont/outlined.css'

import {
  ClearTag,
  CloseIcon,
  Container,
  DownloadIcon,
  Header,
  OptionsContainer,
  Selected,
  Tag,
  Title,
  TopContainer,
} from './styles'

import {
  getDiabetesOverview as getDiabetesOverviewAction,
  getHypertensionAndDyslipidemiaOverview as getHypertensionAndDyslipidemiaOverviewAction,
  getLifeHabitsOverview as getLifeHabitsOverviewAction,
  getOverweightOverview as getOverweightOverviewAction,
  getRisksOverview as getRisksOverviewAction,
  getEngagementBeneficiaryOverview as getEngagementBeneficiaryOverviewAction,
  getEquipmentOverview as getEquipmentOverviewAction
} from '@actions/overview'

import {
  getCountBeneficiariesWithCollection as getCountBeneficiariesWithCollectionAction
} from '@actions/beneficiary'

const ClassificationMetrics = ({
  beneficiaryFilter,
  beneficiaryWithCollectionCount,
  setBeneficiaryFilter,
  getRisksOverview,
  getDiabetesOverview,
  getOverweightOverview,
  getEngagementBeneficiaryOverview,
  getLifeHabitsOverview,
  getHypertensionAndDyslipidemiaOverview,
  companyId,
  programmeId,
  getCountBeneficiariesWithCollection,
  risksData,
  diabetesData,
  overweightData,
  lifeHabitsData,
  hypertensionAndDyslipidemiaData,
  engagementBeneficiaryData,
  getEquipmentOverview,
  equipmentData
}) => {
  const [loading, setLoading] = useState(false)
  const [isLoadingDownload, setIsLoadingDownload] = useState(false)
  const [selected, setSelected] = useState('risk')

  const {
    none,
    low,
    medium,
    high
  } = risksData || 0

  const {
    hasDiabetes,
    amputation,
    diabeticFoot,
    nephropathy,
    type2,
    controlledGlycatedHemoglobin,
    allDiabetes,
    diabeticFootEvaluated
  } = diabetesData || 0
  const { overweight } = overweightData || 0
  const { alcohol, smoking } = lifeHabitsData || 0
  const { activeAccounts, frequentAccessApp } = engagementBeneficiaryData || 0

  const {
    controlledHypertension,
    dyslipidemia,
    hypertension
  } = hypertensionAndDyslipidemiaData || 0

  const onChange = (status) => {
    const aux = { ...beneficiaryFilter }
    aux[status] = !aux[status]
    setBeneficiaryFilter(aux)
  }

  const buttonNotSelectedStyle = {
    backgroundColor: colors.softGreen,
    color: colors.titleStrongBlack,
    width: '20%',
  }
  const buttonSelectedStyle = {
    backgroundColor: colors.primary,
    color: colors.background,
    width: '20%',
  }

  const componentMap = {
    risk: RiskStatus,
    diabetic: DiabetesStatus,
    lifeHabits: LifeHabitsStatus,
    hypertensionAndDyslipidemia: HyperTensionAndDyslipdemiaStatus,
    overweight: OverweigthStatus,
    engagementBeneficiary: engagementBeneficiaryStatus,
    equipment: equipmentStatus
  }

  const getDataOverviewBySelectedState = {
    risk: getRisksOverview,
    diabetic: getDiabetesOverview,
    lifeHabits: getLifeHabitsOverview,
    hypertensionAndDyslipidemia: getHypertensionAndDyslipidemiaOverview,
    overweight: getOverweightOverview,
    engagementBeneficiary: getEngagementBeneficiaryOverview,
    equipment: getEquipmentOverview
  }

  const renderSelected = () => {
    const Component = componentMap[selected]
    return Component
      ? <Component status={beneficiaryFilter} onChange={onChange} loading={loading} /> : null
  }

  const onClear = () => {
    const resetFilter = Object.keys(beneficiaryFilter).reduce((acc, key) => {
      acc[key] = false
      return acc
    }, {})

    setBeneficiaryFilter(resetFilter)
  }

  const checkSelected = () => Object.values(beneficiaryFilter).some((value) => value)

  const renderClearCards = () => {
    return (
      <ClearTag onClick={() => onClear()}>
        Limpar filtros
      </ClearTag>
    )
  }

  const renderCards = () => {
    // Extrai apenas as entradas com valores verdadeiros, excluindo startDate e endDate
    const selectedFilters = Object.entries(beneficiaryFilter)
      .filter(([key, value]) => value && !['startDate', 'endDate'].includes(key))

    // Função para renderizar uma tag individual
    const renderTag = (key) => (
      <Tag key={key}>
        {statusTypes[key]}
        <CloseIcon className="material-icons" onClick={() => onChange(key)}>
          close
        </CloseIcon>
      </Tag>
    )

    return (
      <Selected>
        {selectedFilters.map(([key]) => renderTag(key))}
        {checkSelected() && renderClearCards()}
      </Selected>
    )
  }

  const renderFilter = (types) => {
    return types.map((type) => {
      return (
        <PrimaryButton
          onClick={() => { setSelected(type) }}
          style={selected === type ? buttonSelectedStyle : buttonNotSelectedStyle}
        >
          {statusFilter[type]}
        </PrimaryButton>
      )
    })
  }

  const resolvedParams = programmeId || companyId || null
  const isParamsReady = !companyId || (companyId.companyId != null || companyId.programmeId != null)

  useEffect(() => {
    if (!isParamsReady) return
    getCountBeneficiariesWithCollection(resolvedParams)
  }, [isParamsReady, resolvedParams])

  useEffect(() => {
    if (!isParamsReady) return
    setLoading(true)
    getDataOverviewBySelectedState[selected](resolvedParams, () => {
      setLoading(false)
    })
  }, [selected, isParamsReady, resolvedParams])

  const renderLoadingDownload = () => (
    <Loading color={colors.strongGreen} height={30} width={30} />
  )

  useEffect(() => {
    if (!isParamsReady) return
    if (!diabetesData) {
      setIsLoadingDownload(true)
      getDiabetesOverview(resolvedParams, () => setIsLoadingDownload(false))
    }
    if (!overweightData) {
      setIsLoadingDownload(true)
      getOverweightOverview(resolvedParams, () => setIsLoadingDownload(false))
    }
    if (!lifeHabitsData) {
      setIsLoadingDownload(true)
      getLifeHabitsOverview(resolvedParams, () => setIsLoadingDownload(false))
    }
    if (!hypertensionAndDyslipidemiaData) {
      setIsLoadingDownload(true)
      getHypertensionAndDyslipidemiaOverview(resolvedParams, () => setIsLoadingDownload(false))
    }
    if (!beneficiaryWithCollectionCount) {
      setIsLoadingDownload(true)
      getCountBeneficiariesWithCollection(resolvedParams, () => setIsLoadingDownload(false))
    }
    if (!equipmentData) {
      setIsLoadingDownload(true)
      getEquipmentOverview(resolvedParams, () => setIsLoadingDownload(false))
    }
    if (!engagementBeneficiaryData) {
      setIsLoadingDownload(true)
      getEngagementBeneficiaryOverview(resolvedParams, () => setIsLoadingDownload(false))
    }
  }, [
    isParamsReady,
    resolvedParams,
    diabetesData,
    overweightData,
    lifeHabitsData,
    hypertensionAndDyslipidemiaData,
    equipmentData,
    engagementBeneficiaryData
  ])

  const insertData = (worksheet, startRow, col, data) => {
    const sheet = worksheet
    data.forEach((value, index) => {
      sheet.getCell(`${col}${startRow + index}`).value = value
    })
  }

  const handleDownload = () => {

    fetch(excelFile)
      .then((response) => {
        return response.arrayBuffer()
      })
      .then((data) => {
        const workbook = new ExcelJS.Workbook()
        return workbook.xlsx.load(data)
      })
      .then((workbook) => {
        const risksIndicators = {
          D: [
            `${getMeterValue(high, beneficiaryWithCollectionCount)}%`,
            `${getMeterValue(medium, beneficiaryWithCollectionCount)}%`,
            `${getMeterValue(low, beneficiaryWithCollectionCount)}%`,
            `${getMeterValue(none, beneficiaryWithCollectionCount)}%`
          ],
          F: Array(4).fill(beneficiaryWithCollectionCount),
          E: [high, medium, low, none],
        }

        const promoPrevIndicators = {
          D: [
            `${getMeterValue(hasDiabetes, beneficiaryWithCollectionCount)}%`,
            `${getMeterValue(type2, allDiabetes)}%`,
            `${getMeterValue(amputation, beneficiaryWithCollectionCount)}%`,
            `${getMeterValue(controlledGlycatedHemoglobin, beneficiaryWithCollectionCount)}%`,
            `${getMeterValue(diabeticFoot, beneficiaryWithCollectionCount)}%`,
            `${getMeterValue(diabeticFootEvaluated, beneficiaryWithCollectionCount)}%`,
            `${getMeterValue(nephropathy, beneficiaryWithCollectionCount)}%`,
            `${getMeterValue(alcohol, beneficiaryWithCollectionCount)}%`,
            `${getMeterValue(smoking, beneficiaryWithCollectionCount)}%`,
            `${getMeterValue(hypertension, beneficiaryWithCollectionCount)}%`,
            `${getMeterValue(controlledHypertension, beneficiaryWithCollectionCount)}%`,
            `${getMeterValue(dyslipidemia, beneficiaryWithCollectionCount)}%`,
            `${getMeterValue(overweight, beneficiaryWithCollectionCount)}%`,
            `${getMeterValue(activeAccounts, beneficiaryWithCollectionCount)}%`,
            `${getMeterValue(frequentAccessApp, activeAccounts)}%`,
          ],
          F: [
            ...Array(14).fill(beneficiaryWithCollectionCount),
            activeAccounts
          ],
          E: [
            hasDiabetes, type2, amputation, controlledGlycatedHemoglobin, diabeticFoot,
            diabeticFootEvaluated, nephropathy, alcohol, smoking, hypertension,
            controlledHypertension, dyslipidemia, overweight, activeAccounts, frequentAccessApp
          ],
        }

        const RisksSheet = workbook.getWorksheet(1)
        const PromoPrevRisks = workbook.getWorksheet(2)

        insertData(RisksSheet, 8, 'D', risksIndicators.D)
        insertData(RisksSheet, 8, 'F', risksIndicators.F)
        insertData(RisksSheet, 8, 'E', risksIndicators.E)

        insertData(PromoPrevRisks, 8, 'D', promoPrevIndicators.D)
        insertData(PromoPrevRisks, 8, 'F', promoPrevIndicators.F)
        insertData(PromoPrevRisks, 8, 'E', promoPrevIndicators.E)

        return workbook.xlsx.writeBuffer()
      })
      .then((buffer) => {
        const blob = new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
        saveAs(blob, 'Indicadores.xlsx')
      })
      .catch((error) => {
        console.error('[handleDownload] Erro ao gerar ou baixar o arquivo Excel:', error)
      })
  }

  // () => setSpreadSheetRisksModal(true)
  return (
    <Card
      style={{
        display: 'flex',
        flexDirection: 'column',
        padding: '16px',
        width: '65%',
        flex: '0 0 97.5%',
      }}
    >
      <Container>
        <Header>
          <TopContainer>
            <Title>Painel</Title>
            {isLoadingDownload ? renderLoadingDownload()
              : (
                <DownloadIcon
                  className="material-icons-outlined"
                  onClick={() => handleDownload()}
                >
                  file_download
                </DownloadIcon>
              )}
          </TopContainer>
          {renderCards()}
        </Header>
        <OptionsContainer>
          {renderFilter(['risk', 'diabetic', 'lifeHabits', 'hypertensionAndDyslipidemia', 'overweight', 'equipment', 'engagementBeneficiary'])}
        </OptionsContainer>
      </Container>
      {renderSelected()}
    </Card>
  )
}

// <CSVLink
//                   // data={csvContent}
//   filename="Relatorio_de_riscos_saude_preventiva"
//   ref={csvRef}
//   target="_blank"
// />

ClassificationMetrics.propTypes = {
  companyId: PropTypes.number,
  programmeId: PropTypes.number,
  getEquipmentOverview: PropTypes.func.isRequired,
  beneficiaryFilter: PropTypes.object.isRequired,
  beneficiaryWithCollectionCount: PropTypes.number,
  setBeneficiaryFilter: PropTypes.func.isRequired,
  getRisksOverview: PropTypes.func.isRequired,
  getDiabetesOverview: PropTypes.func.isRequired,
  getOverweightOverview: PropTypes.func.isRequired,
  getLifeHabitsOverview: PropTypes.func.isRequired,
  getEngagementBeneficiaryOverview: PropTypes.func.isRequired,
  getHypertensionAndDyslipidemiaOverview: PropTypes.func.isRequired,
  getCountBeneficiariesWithCollection: PropTypes.func.isRequired,
  equipmentData: PropTypes.shape({
    activeLoans: PropTypes.number,
    diabeticsWithGlucometer: PropTypes.number,
    equipmentInUse: PropTypes.number,
  }),
  risksData: PropTypes.shape({
    none: PropTypes.number,
    medium: PropTypes.number,
    low: PropTypes.number,
    high: PropTypes.number,
  }),
  diabetesData: PropTypes.shape({
    hasDiabetes: PropTypes.number,
    amputation: PropTypes.number,
    diabeticFoot: PropTypes.number,
    nephropathy: PropTypes.number,
    type2: PropTypes.number,
    controlledGlycatedHemoglobin: PropTypes.number,
    diabeticFootEvaluated: PropTypes.number,
  }),
  overweightData: PropTypes.shape({
    overweight: PropTypes.number,
    overweightInRecovery: PropTypes.number,
  }),
  lifeHabitsData: PropTypes.shape({
    alcohol: PropTypes.number,
    smoking: PropTypes.number,
  }),
  hypertensionAndDyslipidemiaData: PropTypes.shape({
    controlledHypertension: PropTypes.number,
    dyslipidemia: PropTypes.number,
    hypertension: PropTypes.number,
  }),
  engagementBeneficiaryData: PropTypes.shape({
    activeAccounts: PropTypes.number,
    frequentAccessApp: PropTypes.number,
  })
}

ClassificationMetrics.defaultProps = {
  beneficiaryWithCollectionCount: null,
  companyId: null,
  programmeId: null,
  risksData: null,
  diabetesData: null,
  overweightData: null,
  lifeHabitsData: null,
  hypertensionAndDyslipidemiaData: null,
  engagementBeneficiaryData: null,
  equipmentData: null,
}

const mapStateToProps = ({ overview, beneficiary }) => ({
  beneficiaryWithCollectionCount: beneficiary.beneficiaryWithCollectionCount,
  risksData: overview.risksData,
  diabetesData: overview.diabetesData,
  overweightData: overview.overweightData,
  lifeHabitsData: overview.lifeHabitsData,
  hypertensionAndDyslipidemiaData: overview.hypertensionAndDyslipidemiaData,
  engagementBeneficiaryData: overview.engagementBeneficiaryData,
  equipmentData: overview.equipmentData
})

const mapDispatchToProps = (dispatch) => bindActionCreators(
  {
    getRisksOverview: getRisksOverviewAction,
    getDiabetesOverview: getDiabetesOverviewAction,
    getOverweightOverview: getOverweightOverviewAction,
    getLifeHabitsOverview: getLifeHabitsOverviewAction,
    getHypertensionAndDyslipidemiaOverview: getHypertensionAndDyslipidemiaOverviewAction,
    getEngagementBeneficiaryOverview: getEngagementBeneficiaryOverviewAction,
    getCountBeneficiariesWithCollection: getCountBeneficiariesWithCollectionAction,
    getEquipmentOverview: getEquipmentOverviewAction
  },
  dispatch
)

export default connect(mapStateToProps, mapDispatchToProps)(ClassificationMetrics)
